import { ApiError } from '@l2u/ui/lib/api.ts';
import type { CompletionReason } from '@l2u/shared/progress';

/**
 * POST /me/submit rejects an incomplete questionnaire with
 * `AppError('unprocessable', 'Questionnaire is not complete', { missing,
 * missing_company_info })` (apps/api/src/routes/me.ts:796-801). The generic
 * ErrorMessage renders only `error.message`, so that detail was being dropped
 * and the vendor saw a block with nothing to act on.
 *
 * This pulls the detail back out and names the rows. Returns empty lists for any
 * other error so callers can fall back to the plain message.
 */
export type SubmitBlockers = {
  questions: string[];
  companyInfo: string[];
};

const EMPTY: SubmitBlockers = { questions: [], companyInfo: [] };

// What the vendor has to DO about each reason — phrased as the missing thing,
// not the internal reason code.
// Exhaustive by construction: Record<CompletionReason, …> means a new reason
// added to the engine fails the build here rather than silently rendering a
// bare label.
const NEEDS: Record<CompletionReason, string> = {
  no_answer: 'needs an answer',
  upload_required: 'needs a document',
  adoption_required: 'needs a document',
  expiry_required: 'needs an expiry date',
  video_unwatched: 'needs the video watched',
};

function isMissingEntry(value: unknown): value is { question_id: string; reason: string } {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { question_id?: unknown }).question_id === 'string'
  );
}

export function submitBlockers(
  error: unknown,
  labelById: ReadonlyMap<string, string>,
): SubmitBlockers {
  if (!(error instanceof ApiError)) return EMPTY;
  const details = error.details;
  if (typeof details !== 'object' || details === null) return EMPTY;

  const rawMissing = (details as { missing?: unknown }).missing;
  const questions = Array.isArray(rawMissing)
    ? rawMissing.filter(isMissingEntry).map((entry) => {
        // A question the vendor cannot currently see (retired, or filtered out
        // of this questionnaire) has no label — name it generically rather than
        // rendering a raw uuid.
        const label = labelById.get(entry.question_id) ?? 'A question';
        const needs = NEEDS[entry.reason as CompletionReason];
        return needs ? `${label} — ${needs}` : label;
      })
    : [];

  const rawInfo = (details as { missing_company_info?: unknown }).missing_company_info;
  const companyInfo = Array.isArray(rawInfo) ? rawInfo.filter((f): f is string => typeof f === 'string') : [];

  return { questions, companyInfo };
}
